import React, { FunctionComponent, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { IconButton } from '../../atoms/iconButton/IconButton'
import { ThumbDirection, ThumbIcon } from '../../atoms/icons/ThumbIcon'
import { tID } from '../../utils/utils'

const VERTICAL_BUTTON_STYLE = { paddingVertical: 8 }

const Container = styled.View<{ vertical?: boolean }>(({ theme, vertical }) => ({
  flexDirection: vertical ? 'column' : 'row',
  marginTop: vertical ? 0 : theme.spacing['4px'],
  gap: vertical ? 4 : 0,
}))

export interface MessageFeedbackProps {
  onPress: (value: string | null) => Promise<void>
  value?: string
  vertical?: boolean
}

enum FeedbackValue {
  UP = '1',
  DOWN = '0',
}

export const MessageFeedback: FunctionComponent<MessageFeedbackProps> = ({ onPress, value = 0, vertical = false }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const upSelected = value === FeedbackValue.UP
  const downSelected = value === FeedbackValue.DOWN
  // Disable both buttons while a feedback request is in flight so rapid repeated
  // clicks can't fire duplicate/conflicting requests.
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Synchronous guard alongside isSubmitting: on a fast double-tap a second press
  // can fire before React re-renders the button as disabled, and the isSubmitting
  // state read in this closure would still be stale. The ref blocks that second
  // press immediately, in the same tick.
  const inFlightRef = useRef(false)

  const handlePress = async (val: string | null) => {
    if (inFlightRef.current) {
      return
    }
    inFlightRef.current = true
    setIsSubmitting(true)
    try {
      await onPress(val)
    } finally {
      inFlightRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <Container vertical={vertical} testID={tID('MessageFeedback')}>
      <IconButton
        leftIcon={<ThumbIcon isFilled={upSelected} />}
        onPress={() => handlePress(upSelected ? null : FeedbackValue.UP)}
        disabled={isSubmitting}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Positive Feedback',
          description: 'Button for leaving positive feedback on ai response',
        })}
        textAlign='center'
        testID={tID(`MessageFeedback-positiveButton-${upSelected ? 'selected' : 'unselected'}`)}
        iconColor={upSelected ? colors.iconActive : undefined}
        style={vertical ? VERTICAL_BUTTON_STYLE : undefined}
      />
      <IconButton
        leftIcon={<ThumbIcon direction={ThumbDirection.DOWN} isFilled={downSelected} />}
        onPress={() => handlePress(downSelected ? null : FeedbackValue.DOWN)}
        disabled={isSubmitting}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Negative Feedback',
          description: 'Button for leaving negative feedback on ai response',
        })}
        textAlign='center'
        testID={tID(`MessageFeedback-negativeButton-${downSelected ? 'selected' : 'unselected'}`)}
        iconColor={downSelected ? colors.iconActive : undefined}
        style={vertical ? VERTICAL_BUTTON_STYLE : undefined}
      />
    </Container>
  )
}
